import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Menu, X, Sun, LogOut, Facebook, Instagram, Youtube, ChevronDown } from "lucide-react";
import { fetchArtists, getToken, clearToken } from "@/lib/api";
import CookieConsent from "@/components/CookieConsent";

const PRIMARY_NAV = [
  { to: "/c/music", label: "Music" },
  { to: "/c/athletes", label: "Athletes" },
  { to: "/c/fashion", label: "Fashion" },
  { to: "/watch", label: "Watch" },
  { to: "/rankings", label: "Rankings" },
];

const MORE_NAV = [
  { to: "/c/shoes", label: "Shoes" },
  { to: "/c/cars", label: "Cars" },
  { to: "/c/travel", label: "Travel" },
  { to: "/c/technology", label: "Tech" },
  { to: "/c/business", label: "Business" },
  { to: "/c/media", label: "Media" },
  { to: "/c/food", label: "Food" },
  { to: "/community", label: "Community" },
  { to: "/advertorials", label: "Sponsored" },
];

const NAV = [...PRIMARY_NAV, ...MORE_NAV];

const SOCIALS = {
  facebook: "https://www.facebook.com/profile.php?id=61591381925699",
  instagram: "https://www.instagram.com/nerosunmedia",
  youtube: "https://www.youtube.com/@nerosunmedia",
};

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
    <Sun className="w-6 h-6 text-signal group-hover:rotate-90 transition-transform duration-500" strokeWidth={2.5} />
    <span className="font-anton text-2xl tracking-tight leading-none">
      NERO<span className="text-signal">SUN</span>
    </span>
  </Link>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [trending, setTrending] = useState([]);
  const [authed, setAuthed] = useState(!!getToken());
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchArtists().then((d) => setTrending(d.slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
    setAuthed(!!getToken());
  }, [pathname]);

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-6">
          <div className="shrink-0"><Logo /></div>
          <nav className="hidden xl:flex items-center gap-4 min-w-0">
            {PRIMARY_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-widest transition-colors hover:text-signal whitespace-nowrap ${
                    isActive ? "text-signal" : "text-foreground/80"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}

            {/* More dropdown */}
            <div className="relative group">
              <button
                data-testid="nav-more"
                className="font-mono text-xs uppercase tracking-widest text-foreground/80 hover:text-signal transition-colors inline-flex items-center gap-1 whitespace-nowrap"
              >
                More <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <div className="absolute right-0 top-full pt-3 hidden group-hover:block z-50">
                <div className="bg-black/95 backdrop-blur-xl border border-white/15 min-w-[190px] py-2 shadow-2xl" data-testid="nav-more-menu">
                  {MORE_NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={`nav-${n.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors hover:bg-secondary hover:text-signal ${
                          isActive ? "text-signal" : "text-foreground/80"
                        }`
                      }
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/advertise"
              data-testid="nav-advertise"
              className="font-mono text-xs uppercase tracking-widest border border-white/20 text-foreground/80 px-3 py-1.5 hover:border-signal hover:text-signal transition-colors whitespace-nowrap"
            >
              Advertise
            </Link>
            <Link
              to="/studio"
              data-testid="nav-studio"
              className="font-mono text-xs uppercase tracking-widest bg-signal text-white px-3 py-1.5 hover:bg-signal-hover transition-colors whitespace-nowrap"
            >
              NS Studio
            </Link>
            <div className="hidden xl:flex items-center gap-3 pl-3 border-l border-white/15">
              <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="text-foreground/70 hover:text-signal transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
              <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="text-foreground/70 hover:text-signal transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
              <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube" className="text-foreground/70 hover:text-signal transition-colors" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
            </div>
            {authed && (
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                title="Log out"
                className="font-mono text-xs uppercase tracking-widest text-foreground/70 hover:text-signal transition-colors inline-flex items-center gap-1"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            )}
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {/* Trending marquee */}
        <div className="border-t border-white/10 bg-signal text-white">
          <Marquee speed={50} gradient={false} className="py-1.5">
            <span className="font-mono text-[11px] uppercase tracking-widest flex items-center">
              <span className="font-bold mr-6">NERO SUN // GLOBAL RANKINGS</span>
              {trending.map((a) => (
                <span key={a.slug} className="mr-6 opacity-90">
                  #{a.rank} {a.name} · {a.overall}
                </span>
              ))}
              <span className="font-bold mr-6">WHERE HIP-HOP & EVERYTHING CONVERGES</span>
            </span>
          </Marquee>
        </div>

        {open && (
          <div className="xl:hidden border-t border-white/10 bg-black/95 px-5 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className="font-mono text-sm uppercase tracking-widest text-foreground/85 hover:text-signal"
              >
                {n.label}
              </Link>
            ))}
            <Link to="/studio" className="font-mono text-sm uppercase tracking-widest text-signal">
              NS Studio
            </Link>
            <Link to="/advertise" data-testid="mobile-nav-advertise" className="font-mono text-sm uppercase tracking-widest text-foreground/85 hover:text-signal">
              Advertise
            </Link>
            <div className="flex items-center gap-4 pt-2">
              <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" className="text-foreground/70 hover:text-signal" aria-label="Facebook"><Facebook className="w-5 h-5" /></a>
              <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" className="text-foreground/70 hover:text-signal" aria-label="Instagram"><Instagram className="w-5 h-5" /></a>
              <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" className="text-foreground/70 hover:text-signal" aria-label="YouTube"><Youtube className="w-5 h-5" /></a>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <CookieConsent />

      <footer className="border-t border-white/10 mt-24">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <div className="flex flex-col md:flex-row justify-between gap-10">
            <div className="max-w-sm">
              <Logo />
              <p className="text-muted-foreground text-sm mt-4 leading-relaxed">
                A digital magazine charting the global convergence of hip-hop,
                fashion, athletes, cars, shoes and travel — from an urban perspective.
              </p>
              <div className="flex items-center gap-4 mt-6">
                <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/15 text-foreground/70 hover:border-signal hover:text-signal transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/15 text-foreground/70 hover:border-signal hover:text-signal transition-colors" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/15 text-foreground/70 hover:border-signal hover:text-signal transition-colors" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-14 gap-y-2">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="font-mono text-xs uppercase tracking-widest text-foreground/70 hover:text-signal py-1"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              © {new Date().getFullYear()} Nero Sun
            </span>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link to="/about" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal">
                About
              </Link>
              <Link to="/contact" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal">
                Contact
              </Link>
              <Link to="/privacy" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal">
                Privacy
              </Link>
              <Link to="/advertise" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal">
                Advertise
              </Link>
              <Link to="/admin/leads" data-testid="footer-admin-link" className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-signal">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
